import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {
  AssetMetaInterface,
  ComputedPageItem,
  ObjectInterface,
} from '@taopix/taopix-core/dist/types/types';
import {
  Button,
  ButtonGrid,
  CloseIcon,
  Heading,
  Horizontal,
  MaskIcon,
  MoreIcon,
  PopOut,
  ThemeName,
  Vertical,
} from '@taopix/taopix-design-system';
import {
  AIMode,
  AssetType,
  BoxType,
  BoxTypeShape,
  Common,
  CoreEvent,
  MaskFileType,
  NumBool,
} from '@taopix/taopix-core';
import {GenericListItem} from '../../types';
import {useTaopix} from '../Context/TaopixContext';
import {useExperience} from '../Experience/ExperienceProvider';
import useFilter from './useFilter';
import MasksList from '../MasksList/MasksList';
import classNames from 'classnames';
import {filterObject} from '../../Utils/Common';
import {KeyboardKeys} from '../../Enums';
import {useUiState} from '../Context/UserInterface/UserInterfaceContext';
import ToolPaletteSection from '../ToolPalette/ToolPaletteSection';
import PaletteInformationMessage from './BoxSettings/PaletteInformationMessage';

export type MaskPaletteProps = {
  sectionKey: string;
  item?: ComputedPageItem;
};

enum Mode {
  None = 0,
  Clear = 1,
  PageItemSelected = 2,
  MaskSelected = 3,
  InvalidPageItem = 4,
  MaskSelectedFromInvalidPageItem = 5,
}

type MasksExperienceValues = {
  categoryFilter: boolean;
  clearMaskButton: boolean;
};

const MaskPalette = React.forwardRef(
  ({sectionKey, item}: MaskPaletteProps, ref: React.ForwardedRef<HTMLButtonElement>) => {
    const app = useTaopix();
    const ui = useUiState();
    const {experience} = useExperience();
    const {backgroundOptions, pictureBoxOptions, shapeBoxOptions} = experience.EDITOR;
    const {t} = useTranslation(['toolpalette', 'helptips']);
    const isSmallScreen = ui.state.isSmallScreen;
    const masks = useSelector<any>(state =>
      Object.values<AssetMetaInterface>(state.project.systemAssets).filter(a => {
        return a.assetType === AssetType.Mask && !a.hiddenFromUser && a.active;
      })
    ) as AssetMetaInterface[];
    const [filter, setFilter] = useFilter<AssetMetaInterface>();
    const [maskFilterPopOutOpen, setMaskFilterPopOutOpen] = useState<boolean>(false);
    const [selectedMaskAsset, setSelectedMaskAsset] = useState<AssetMetaInterface | null>(null);
    const [loaded, setLoaded] = useState<boolean>(false);
    const [filterCount, setFilterCount] = useState<number>(0);
    const [removeMaskIsDisabled, setRemoveMaskIsDisabled] = useState<boolean>(true);
    const paletteOpen = ui.state.palette.section?.includes(sectionKey) || false;
    const filterButtonRef = useRef<HTMLButtonElement>(null);
    const closeButtonRef = useRef<HTMLButtonElement>(null);
    const hasMasks = Object.keys(masks).length > 0;
    const [showSection, setShowSection] = useState<boolean>(false);
    const [mode, setMode] = useState(Mode.None);
    const [disableSection, setDisableSection] = useState<boolean>(false);
    const [notAvailableMessage, setNotAvailableMessage] = useState<string>('');
    const [paletteOptions, setPaletteOptions] = useState<MasksExperienceValues>({
      categoryFilter: true,
      clearMaskButton: true,
    });
    // const canDeviceDrag = matchMedia('(pointer: fine)').matches;

    /**
     * Checks if a mask can be applied to the passed page item.
     *
     * @param item The item to check.
     * @returns True if a mask can be applied this this page item.
     */
    const isValidItem = (item?: ComputedPageItem) => {
      if (!item || item.ref === -1) return false;
      // Item is valid if:
      // - It's a picture box
      // - Is a user picture box.
      // - It's a shape (but not a line)
      // - It's a background image
      const boxType = item?.type;
      return (
        (BoxType.Image === boxType ||
          (BoxType.Shape === boxType && BoxTypeShape.Line !== item?.shape)) &&
        (AssetType.User === (item?.asset?.assetType || AssetType.User) ||
          AssetType.Background === item?.asset?.assetType)
      );
    };

    /**
     * Fires an ADD_ASSET_SOURCE event.
     *
     * @param payload Payload to override the default one.
     */
    const fireAddAssetSourceEvent = (payload: ObjectInterface): void => {
      app.core
        .dispatch('PROJECT.ADD_ASSET_SOURCE', {
          asset: null,
          aiMode: AIMode.NotAvailable,
          rotation: null,
          dynamicBehaviour: false,
          autoSelect: false,
          atSource: true,
          ...payload,
        })
        .catch(error => {
          console.error(error);
        });
    };

    // get a list of categories
    const categories =
      masks?.reduce((accumulator: {[key: string]: GenericListItem}, mask: AssetMetaInterface) => {
        const category = Common.getCurrentLocaleString(mask.categoryName as string, true);
        if (category !== '' && !accumulator[category]) {
          accumulator[category] = {
            label: `${category}`,
            value: `${mask.categoryName}`,
            key: `${Object.keys(accumulator).length}`,
          };
        }
        return accumulator;
      }, {}) || {};

    const hasCategories = Object.values(categories).length > 1;

    const [gridState, setGridState] = useState<ObjectInterface>({
      orientation: 'vertical',
      columns: 2, // The column count is only used if orientation is vertical; in horizontal mode there are infinite columns
      tileSize: 60, // The tileSize is only used if orientation is horizontal; in vertical mode it is determined by the column count
      gap: 10,
    });

    useEffect(() => {
      setGridState({
        ...gridState,
        gap: isSmallScreen ? 5 : 10,
        orientation: isSmallScreen ? 'horizontal' : 'vertical',
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSmallScreen]);

    useEffect(() => {
      const validItem = isValidItem(item);
      switch (mode) {
        case Mode.Clear: {
          // if there is no item, remove the asset source overlay
          if (!validItem) {
            fireAddAssetSourceEvent({});
          }
          setSelectedMaskAsset(null);
          setMode(Mode.None);
          break;
        }
        case Mode.PageItemSelected: {
          if (validItem) {
            const itemHasMask = item?.maskref !== '' && item?.maskfiletype === MaskFileType.IsMask;
            setSelectedMaskAsset(
              (itemHasMask ? masks.find(mask => mask.assetRef === item?.maskref) : null) || null
            );
          } else {
            setSelectedMaskAsset(null);
          }
          setMode(Mode.None);
          break;
        }
        case Mode.MaskSelected: {
          if (selectedMaskAsset === null) {
            // Same mask was selected, so remove it.
            setMode(Mode.Clear);
          } else if (item?.ref === -1) {
            // There is no item selected, display the overlays.
            fireAddAssetSourceEvent({asset: selectedMaskAsset});
            setMode(Mode.None);
          } else if (validItem) {
            // The item is valid, apply the mask directly.
            fireAddAssetSourceEvent({asset: selectedMaskAsset});
            setMode(Mode.None);
          } else if (!validItem) {
            // Can't apply mask to page item, remove the selection and show the overlays.
            setMode(Mode.InvalidPageItem);
          }
          break;
        }
        case Mode.InvalidPageItem: {
          // Deselect the page item, MaskSelectedFromInvalidPageItem will then be triggered from a useEffect.
          app.core.dispatch('SHAPE.DESELECT', null).catch(error => {
            console.error(error);
          });
          break;
        }
        case Mode.MaskSelectedFromInvalidPageItem: {
          // Display the overlays.
          fireAddAssetSourceEvent({asset: selectedMaskAsset});
          break;
        }
      }
    }, [mode]);

    useEffect(() => {
      const validItem = isValidItem(item);
      if (paletteOpen && validItem) {
        setMode(Mode.PageItemSelected);
      } else if (paletteOpen && !validItem && Mode.InvalidPageItem === mode) {
        setMode(Mode.MaskSelectedFromInvalidPageItem);
      } else if (paletteOpen) {
        setMode(Mode.Clear);
      }

      const hasMask =
        -1 !== item?.ref && '' !== item?.maskref && MaskFileType.IsMask === item?.maskfiletype;
      setRemoveMaskIsDisabled(!hasMask);
    }, [item?.ref, item?.maskref, item?.maskfiletype]);

    useEffect(() => {
      const tokens: string[] = [];

      // if there is a selected mask and palette closed remove the selection
      if (
        (!paletteOpen && typeof selectedMaskAsset?.assetRef === 'string') ||
        typeof selectedMaskAsset?.assetRef === 'number'
      ) {
        setMode(Mode.Clear);
      }

      if (paletteOpen) {
        tokens.push(
          app.core.subscribe(CoreEvent.OUTSIDE_CANVAS_CLICK, (msg: string, data: any) => {
            setMode(Mode.Clear);
          })
        );

        tokens.push(
          app.core.subscribe(CoreEvent.CANVAS_SELECTION_EMPTY, (msg: string, data: any) => {
            if (Mode.InvalidPageItem !== mode) {
              // InvalidPageItem mode will deselect the selected item.
              setMode(Mode.Clear);
            }
          })
        );
      }

      return () => {
        tokens.forEach((token, index) => {
          app.core.unsubscribe(token);
          delete tokens[index];
        });
      };
    }, [paletteOpen, mode]);

    useEffect(() => {
      if (-1 !== item?.ref && paletteOpen) {
        setMode(Mode.PageItemSelected);
      }
    }, [paletteOpen]);

    useEffect(() => {
      if (paletteOpen && !loaded) {
        setLoaded(true);
      }
    }, [paletteOpen, loaded, mode]);

    const isActiveInExperience = (item: ComputedPageItem) => {
      const boxType = item.type || item.type;
      // Return whether or not this option is enabled in the experience for the selected item
      if (
        boxType === BoxType.Image &&
        item?.asset?.assetType !== AssetType.Background &&
        item?.asset?.assetType !== AssetType.ScrapbookImage
      ) {
        setPaletteOptions({
          categoryFilter: pictureBoxOptions.masksCategoryFilter,
          clearMaskButton: pictureBoxOptions.clearMaskButton,
        });
        return pictureBoxOptions.masks;
      }
      if (
        boxType === BoxType.Shape &&
        [BoxTypeShape.Line, BoxTypeShape.Oval, BoxTypeShape.Polygon, BoxTypeShape.Rect].includes(
          item?.shape
        )
      ) {
        setPaletteOptions({
          categoryFilter: shapeBoxOptions.masksCategoryFilter,
          clearMaskButton: shapeBoxOptions.clearMaskButton,
        });
        return shapeBoxOptions.masks;
      }
      if (AssetType.Background === item.hiresfiletype) {
        setPaletteOptions({
          categoryFilter: backgroundOptions.masksCategoryFilter,
          clearMaskButton: backgroundOptions.clearMaskButton,
        });
        return backgroundOptions.masks;
      } else {
        return false;
      }
    };

    useEffect(() => {
      // If there is a selected item
      if (item && item.ref !== -1) {
        // Show or hide this section based on the relevant experience and advanced editing configuration
        setShowSection(isActiveInExperience(item));
        // Enable or disable this section based on the item's protection
        setDisableSection(
          NumBool.True === item?.protectcontents ||
            (item.errors?.imageMissing as boolean) ||
            ui.state.swapActive
        );
        // Set the message to 'not available' in case the section was hidden or disabled
        setNotAvailableMessage(t('toolpalette:str_OptionNotAvailableForSelectedItem'));
      } else {
        // If no item is selected, hide the section button
        setShowSection(false);
        // And show the 'please select an item' message
        setNotAvailableMessage(t('toolpalette:str_MessageSelectAnItemToViewOptions'));
      }
    }, [
      item?.asset,
      item?.asset?.assetType,
      item?.type,
      item?.image,
      item?.shape,
      ui.state.swapActive,
    ]);

    const thumbnailScrollContainerClasses = classNames(
      'flex-1 lg:overflow-y-auto sm:overflow-x-auto'
    );

    const topButtonContainerClasses = classNames('flex', 'space-x-xs', 'pb-md', 'lg:w-full');

    const bottomButtonsContainerClasses = classNames(
      'flex',
      'py-sm',
      'w-[min(400rem,theme(width.bounds))]',
      'self-center',
      'space-x-sm'
    );
    const contentWrapperClasses = classNames([
      'flex',
      'lg:flex-1',
      'px-md',
      'flex-col',
      'min-h-0',
      'sm:h-mobilePaletteContentHeight',
    ]);

    const filtersPopoutClasses = classNames(
      isSmallScreen ? 'sm:w-[min(600rem,theme(width.bounds))]' : 'w-[300rem]'
    );

    const buttonCSS = 'mt-xs ml-xs';

    const filteredMasks = masks.filter(m => filterObject(m, filter));

    const onKeyDown = (event: React.KeyboardEvent) => {
      if (KeyboardKeys.ESCAPE === event.key) {
        closeButtonRef.current?.focus();
      }
    };

    const canFilter = paletteOptions.categoryFilter;

    const removeCurrentMaskButton = paletteOptions.clearMaskButton ? (
      <Button
        label={t('toolpalette:str_LabelRemoveCurrentMask')}
        id={'removeCurrentMaskButton'}
        onClick={() => {
          if (item !== undefined && item?.maskfiletype === MaskFileType.IsMask) {
            app.core.dispatch('SHAPE.CLEAR_MASK', null).catch(error => {
              console.error(error);
            });
            setSelectedMaskAsset(null);
          }
        }}
        buttonStyle={'tertiary'}
        size={!isSmallScreen ? 'small' : 'medium'}
        badgeStyle={ThemeName.Primary}
        className={'flex-1 w-full'}
        disabled={removeMaskIsDisabled}
      />
    ) : (
      <></>
    );

    const filters = useMemo(() => {
      if (!canFilter) {
        return <></>;
      }

      return (
        <>
          {hasCategories && (
            <Button
              label={t('*:str_LabelFilter')}
              ref={filterButtonRef}
              id={'masksFilterButton'}
              onClick={() => {
                setMaskFilterPopOutOpen(!maskFilterPopOutOpen);
              }}
              buttonStyle={'tertiary'}
              badgeStyle={ThemeName.Primary}
              aria-pressed={maskFilterPopOutOpen}
              badgeContent={(filterCount as unknown as number) > 0 ? String(filterCount) : ''}
              startIcon={<MoreIcon />}
              size={ui.state.isSmallScreen ? 'medium' : 'small'}
              hideLabel={true}
              badgePosition={'top-right'}
            />
          )}

          <PopOut
            open={maskFilterPopOutOpen}
            id={'maskFiltersPopout'}
            anchorId={isSmallScreen ? 'masksPalette' : 'masksFilterButton'}
            onClose={() => {
              setMaskFilterPopOutOpen(false);
            }}
            excludeIdFromClickOutside={'masksFilterButton'}
            repositionOnResize={isSmallScreen}
            displayMode={'positioned'}
            offset={isSmallScreen ? {x: 0, y: 0} : {x: 5, y: 0}}
            anchorOrigin={
              isSmallScreen
                ? {vertical: Vertical.Bottom, horizontal: Horizontal.Centre}
                : {vertical: Vertical.Top, horizontal: Horizontal.Right}
            }
            transformOrigin={
              isSmallScreen
                ? {vertical: Vertical.Bottom, horizontal: Horizontal.Centre}
                : {vertical: Vertical.Top, horizontal: Horizontal.Left}
            }
            popDirection={isSmallScreen ? 'up' : 'right'}
            focusFirstElement={true}
            className={filtersPopoutClasses}
            theme={'tpx-theme-toolpaletteContent'}
            themeTip={'Sidebar (Content)'}
            contentTheme={ThemeName.Popouts}
          >
            <div className={'w-full p-sm'} onKeyDown={event => onKeyDown(event)}>
              {/* close button for large screen filter popout */}
              {!isSmallScreen && (
                <div className={'absolute top-1 right-1'}>
                  <Button
                    ref={closeButtonRef}
                    label={t('toolpalette:str_LabelCloseFilterPopout')}
                    hideLabel
                    startIcon={<CloseIcon />}
                    onClick={() => {
                      setMaskFilterPopOutOpen(false);
                      filterButtonRef.current?.focus();
                    }}
                    size={'small'}
                    buttonStyle={'standard'}
                  />
                </div>
              )}
              <Heading level={1} className={'text-md mb-xs'} size={4}>
                {t('toolpalette:str_LabelCategories')}
              </Heading>
              <ButtonGrid>
                <>
                  <Button
                    label={t('*:str_LabelAll')}
                    buttonStyle={'tertiary'}
                    size={'small'}
                    className={buttonCSS}
                    aria-pressed={!filter?.categoryName || filter.categoryName === null}
                    onClick={() => {
                      setFilter([{categoryName: null}]);
                      setFilterCount(0);
                    }}
                  />
                  {Object.values(categories).map((item, index) => {
                    return (
                      <Button
                        key={index}
                        label={item.label}
                        buttonStyle={'tertiary'}
                        size={'small'}
                        className={buttonCSS}
                        onClick={() => {
                          setFilter([{categoryName: item.value}]);
                          setFilterCount(1);
                        }}
                        aria-pressed={filter?.categoryName && filter.categoryName === item.value}
                      />
                    );
                  })}
                </>
              </ButtonGrid>
              {/* Show a cancel button in the popout on small screen */}
              {isSmallScreen && (
                <Button
                  className={'w-full mt-md'}
                  buttonStyle={'secondary'}
                  label={t('*:str_ButtonDone')}
                  size="medium"
                  onClick={() => {
                    setMaskFilterPopOutOpen(false);
                  }}
                />
              )}
            </div>
          </PopOut>
        </>
      );
    }, [canFilter, maskFilterPopOutOpen, categories]);

    // does the experience allow us to show the palette?
    // or if 0 masks in project dont show it in toolbar
    if (!experience.EDITOR.pictureBoxOptions.masks || !hasMasks) {
      return null;
    }

    const sectionButtonClasses = classNames('lg:py-md');

    return (
      <ToolPaletteSection
        id={'masksPalette'}
        sectionKey={sectionKey}
        title={t('str_TitleMasks', {ns: 'toolpalette'})}
        buttonIcon={<MaskIcon />}
        ref={ref}
        buttonClasses={sectionButtonClasses}
        useFocusMode
        subSection={true}
        disabled={disableSection}
        showButton={showSection}
        buttonLayout="horizontal"
        parentselected={true}
      >
        <div className={contentWrapperClasses}>
          {!showSection || disableSection ? (
            // If the palette button has been hidden or disabled (by the experience, box protection or nothing is selected),
            // show the relevant 'unavailable' message in the palette in case the palette was left open when its status changed
            <PaletteInformationMessage message={notAvailableMessage} />
          ) : (
            <>
              {!isSmallScreen && (
                <div id="topButtonsContainer" className={topButtonContainerClasses}>
                  {removeCurrentMaskButton}
                  {filters}
                </div>
              )}
              <div
                className={thumbnailScrollContainerClasses}
                id={'masksListContainer'}
                tabIndex={-1}
              >
                <MasksList
                  masks={filteredMasks}
                  columnGap={5}
                  columns={isSmallScreen ? 4 : 2}
                  selectedMaskAsset={selectedMaskAsset}
                  onClick={(newSelectedMaskAsset: AssetMetaInterface) => {
                    if (newSelectedMaskAsset.assetRef === selectedMaskAsset?.assetRef) {
                      if (selectedMaskAsset?.assetRef === item?.maskref) {
                        app.core.dispatch('SHAPE.CLEAR_MASK', null).catch(error => {
                          console.error(error);
                        });
                      }
                      setSelectedMaskAsset(null);
                    } else {
                      setSelectedMaskAsset(newSelectedMaskAsset);
                    }
                    setMode(Mode.MaskSelected);
                  }}
                  orientation={gridState.orientation}
                />
              </div>
              {isSmallScreen && (
                <div id="bottomButtonsContainer" className={bottomButtonsContainerClasses}>
                  {removeCurrentMaskButton}
                  {filters}
                </div>
              )}
            </>
          )}
        </div>
      </ToolPaletteSection>
    );
  }
);

export default MaskPalette;
